// Shared layout for the customer drill-down reports opened from Total Customers
// (transactions, game cards, referrals, smart points, withdrawals, welfare).
// Renders: BackButton + title/actions, customer header card, stat tiles, filter bar,
// error state and the page body. Also exports small formatting helpers and a styled DataGrid.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import BackButton from './BackButton';
import { usePermissions } from '../context/PermissionContext';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

// "Customer ID" shown to people is the public 10-digit Customer No (CustomerProfile.customerNo),
// never the database id.
export const customerNoOf = (obj) => (obj && typeof obj === 'object' && obj.customerNo) || '—';

// Kept for older callers: pass the customer object; a raw database id string is no longer displayed.
export const formatCustomerId = (objOrId) => {
  if (objOrId && typeof objOrId === 'object') return customerNoOf(objOrId);
  return '—';
};

export const formatINR = (n, digits = 2) => {
  const v = Number(n);
  if (n === null || n === undefined || n === '' || isNaN(v)) return '-';
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

// Accepts ISO strings, Dates, or the legacy "dd/mm/yyyy, hh:mm:ss" (UTC) strings some APIs return.
export const parseApiDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && value.includes('/') && value.includes(',')) {
    const [datePart, timePart = '0:0:0'] = value.split(', ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute, second] = timePart.split(':');
    const d = new Date(Date.UTC(+year, +month - 1, +day, +hour || 0, +minute || 0, +second || 0));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

export const formatISTDateTime = (value) => {
  const d = parseApiDate(value);
  if (!d) return '-';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

export const formatISTDate = (value) => {
  const d = parseApiDate(value);
  if (!d) return '-';
  return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' });
};

export const maskMobile = (mobile) => {
  if (!mobile) return 'N/A';
  const s = String(mobile);
  if (s.includes('*') || s.length <= 4) return s;
  return `${s.slice(0, 2)}${'*'.repeat(Math.max(2, s.length - 4))}${s.slice(-2)}`;
};

export const maskVpa = (vpa) => {
  if (!vpa || typeof vpa !== 'string') return vpa || 'N/A';
  if (vpa.startsWith('CASH-')) return 'Cash (no VPA)';
  const [name, domain] = vpa.split('@');
  if (domain === undefined) {
    if (vpa.length <= 4) return '****';
    return vpa.slice(0, 2) + '*'.repeat(Math.max(2, vpa.length - 4)) + vpa.slice(-2);
  }
  const masked = name.length <= 2 ? `${name[0] || ''}*` : name.slice(0, 2) + '*'.repeat(Math.max(2, name.length - 2));
  return `${masked}@${domain}`;
};

// Readable form of a VPA / ledger key; masked unless the viewer may see sensitive data.
export const displayVpa = (vpa, canViewSensitive) => {
  if (!vpa || vpa === 'N/A') return '-';
  if (typeof vpa === 'string' && vpa.startsWith('CASH-')) return 'Cash (no VPA)';
  return canViewSensitive ? vpa : maskVpa(vpa);
};

// Same rules the reports always used: both dates, start <= end, at most 31 days. Returns an error or ''.
export const validateDateRange = (startDate, endDate, maxDays = 31) => {
  if (!startDate || !endDate) return 'Please select both start and end dates.';
  if (startDate > endDate) return 'Start date cannot be after end date.';
  if (Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) > maxDays) return `Date range cannot exceed ${maxDays} days.`;
  return '';
};

// Unmasked mobile / VPA for whoever holds either customer "viewSensitive" option.
export const useCanViewCustomerSensitive = () => {
  const { can } = usePermissions();
  return can('customers.all.viewSensitive') || can('customers.synopsis.viewSensitive');
};

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */

const STATUS_COLORS = {
  success: 'success', completed: 'success', active: 'success', credited: 'success', credit: 'success', paid: 'success', approved: 'success',
  pending: 'warning', processing: 'warning', initiated: 'warning', unused: 'warning',
  used: 'info', debit: 'info',
  failed: 'error', rejected: 'error', expired: 'default', deactive: 'default', cancelled: 'default', inactive: 'default',
};

export const StatusChip = ({ label, tone }) => {
  const text = label === undefined || label === null || label === '' ? '-' : String(label);
  const color = tone || STATUS_COLORS[text.toLowerCase()] || 'default';
  return (
    <Chip
      label={text}
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 700, textTransform: 'capitalize', minWidth: 72 }}
    />
  );
};

export const EmptyState = ({ title = 'No records found', message, icon }) => (
  <Box sx={{ textAlign: 'center', py: 7, px: 2 }}>
    <Box sx={{ color: '#cbd5e1', mb: 1 }}>{icon || <InboxOutlinedIcon sx={{ fontSize: 52 }} />}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#475569' }}>{title}</Typography>
    {message && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{message}</Typography>
    )}
  </Box>
);

const StatTile = ({ label, value, hint, color = 'primary', icon, loading }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: '100%',
      borderRadius: 3,
      border: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      transition: 'box-shadow .2s',
      '&:hover': { boxShadow: '0 6px 18px rgba(15,23,42,0.06)' },
    }}
  >
    {icon && (
      <Box
        sx={(theme) => {
          const pal = theme.palette[color] || theme.palette.primary;
          return {
            width: 44, height: 44, flexShrink: 0, borderRadius: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(pal.main, 0.12), color: pal.dark || pal.main,
          };
        }}
      >
        {icon}
      </Box>
    )}
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', display: 'block', lineHeight: 1.35 }}>
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={90} height={32} />
      ) : (
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.3 }} noWrap>{value}</Typography>
      )}
      {hint && !loading && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.35 }}>{hint}</Typography>
      )}
    </Box>
  </Paper>
);

export const StatTiles = ({ stats = [], loading }) => {
  if (!stats.length) return null;
  // Tiles share the row evenly and wrap to a new line before a label would be cut off
  return (
    <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))' }}>
      {stats.map((s) => (
        <StatTile key={s.label} {...s} loading={loading} />
      ))}
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Customer header card                                               */
/* ------------------------------------------------------------------ */

const CustomerHeaderCard = ({ customerId, fallbackName }) => {
  const canViewSensitive = useCanViewCustomerSensitive();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        const { data } = await axios.get(`${API_BASE_URL}/customerProfiles/${customerId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (alive) setProfile(data || null);
      } catch (e) {
        if (alive) setProfile(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (customerId) load();
    return () => { alive = false; };
  }, [customerId]);

  const name = (profile && profile.customerName) || fallbackName || 'Customer';
  const status = (profile && profile.customerStatus) || '';
  const vpas = ((profile && profile.customerVPA) || []).map((v) => (typeof v === 'object' ? v.vpa : v)).filter(Boolean);
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('') || 'C';

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 2.5,
        borderRadius: 3,
        border: '1px solid #fde3b0',
        background: 'linear-gradient(120deg, #fff7e6 0%, #ffffff 60%)',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontWeight: 800, fontSize: 20 }}>
          {loading ? '' : initials}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {loading ? (
            <>
              <Skeleton width={200} height={30} />
              <Skeleton width={320} height={24} />
            </>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>{name}</Typography>
                {status && (
                  <StatusChip label={status === 'deActive' ? 'Inactive' : status} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Tooltip title="Customer ID">
                  <Chip
                    icon={<BadgeOutlinedIcon />}
                    label={`ID: ${customerNoOf(profile)}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontWeight: 700, fontFamily: 'monospace', bgcolor: '#fff' }}
                  />
                </Tooltip>
                {profile && profile.customerMobile && (
                  <Chip
                    icon={<PhoneIphoneIcon />}
                    label={canViewSensitive ? profile.customerMobile : maskMobile(profile.customerMobile)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, bgcolor: '#fff' }}
                  />
                )}
                {vpas.slice(0, 2).map((v) => (
                  <Chip
                    key={v}
                    icon={<AccountBalanceIcon />}
                    label={displayVpa(v, canViewSensitive)}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 600, bgcolor: '#fff' }}
                  />
                ))}
                {vpas.length > 2 && (
                  <Tooltip title={vpas.slice(2).map((v) => displayVpa(v, canViewSensitive)).join(', ')}>
                    <Chip label={`+${vpas.length - 2} more VPA`} size="small" sx={{ fontWeight: 600 }} />
                  </Tooltip>
                )}
              </Stack>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

/* ------------------------------------------------------------------ */
/*  Styled DataGrid                                                    */
/* ------------------------------------------------------------------ */

export const ReportGrid = ({ rows, columns, loading, emptyTitle, emptyMessage, height, getRowHeight, pageSize = 10, ...rest }) => {
  const NoRows = () => <EmptyState title={emptyTitle || 'No records found'} message={emptyMessage} />;
  return (
    <Box sx={{ width: '100%', height: height || undefined }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight={!height}
        initialState={{ pagination: { paginationModel: { pageSize, page: 0 } } }}
        pageSizeOptions={[10, 20, 50, 100]}
        disableRowSelectionOnClick
        getRowHeight={getRowHeight}
        slots={{ noRowsOverlay: NoRows }}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          bgcolor: '#fff',
          '--DataGrid-overlayHeight': '240px',
          '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader': { backgroundColor: '#f8fafc' },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: '#334155' },
          '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', color: '#0f172a' },
          '& .MuiDataGrid-row:hover': { backgroundColor: '#fffaf0' },
        }}
        {...rest}
      />
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/*  Shell                                                              */
/* ------------------------------------------------------------------ */

const CustomerReportShell = ({
  title,
  subtitle,
  icon,
  customerId,
  customerName,
  actions,
  filters,
  stats,
  statsLoading,
  error,
  onRetry,
  children,
}) => (
  <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1 }}>
      <BackButton />
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon && <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>}
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>{title}</Typography>
        </Stack>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">{actions}</Stack>
      )}
    </Stack>

    <CustomerHeaderCard customerId={customerId} fallbackName={customerName} />

    {filters && (
      <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
        {filters}
      </Paper>
    )}

    {error && (
      <Alert
        severity="error"
        sx={{ mb: 2.5, borderRadius: 2 }}
        action={onRetry ? (
          <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>Retry</Button>
        ) : null}
      >
        {error}
      </Alert>
    )}

    <StatTiles stats={stats || []} loading={statsLoading} />

    {children}
  </Box>
);

// Start / end date pickers (dd-MM-yyyy, no future dates) plus any extra controls on the same row.
export const DateRangeFilter = ({ startDate, endDate, onStartChange, onEndChange, children }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={onStartChange}
          renderInput={(params) => <TextField size="small" fullWidth {...params} />}
          maxDate={new Date()}
          inputFormat="dd-MM-yyyy"
          disableFuture
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={onEndChange}
          renderInput={(params) => <TextField size="small" fullWidth {...params} />}
          maxDate={new Date()}
          inputFormat="dd-MM-yyyy"
          disableFuture
        />
      </Grid>
      {React.Children.toArray(children).filter(Boolean).map((child, i) => (
        <Grid item xs={12} sm={6} md={3} key={i}>{child}</Grid>
      ))}
    </Grid>
  </LocalizationProvider>
);

export const SectionCard = ({ title, subtitle, right, children, sx }) => (
  <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3, border: '1px solid #e2e8f0', mb: 2.5, ...sx }}>
    {(title || right) && (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }} flexWrap="wrap" useFlexGap spacing={1}>
        <Box>
          {title && <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>{title}</Typography>}
          {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        </Box>
        {right}
      </Stack>
    )}
    {children}
  </Paper>
);

export default CustomerReportShell;
